import {
  useCallback,
  useRef,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';

import { getWrapperMaxWidth } from '@/cms/utils';
import { cn } from '@/lib/utils';

import { useMediaQuery } from '../hooks/useMediaQuery';
import { HEADER_LG_MEDIA_QUERY } from '../utils';
import { HeaderItem, HeaderItemProps } from './HeaderItem';
import { MenuAccordionContent } from './MenuAccordionContent';
import { MenuGroup, MenuGroupProps } from './MenuGroup';
import { MenuPromoBanner } from './MenuPromoBanner';

export type MainLinkItemProps = {
  link: {
    item: Pick<
      HeaderItemProps,
      'title' | 'badge' | 'icon' | 'showDropdown' | 'href'
    >;
    menu: MenuGroupProps[];
  };
  isActive: boolean;
  isHovering: boolean;
  onLinkHover: (title?: string) => void;
  onLinkHoverCancel: (title?: string) => void;
  onLinkClick?: (
    params: { title: string } | { url: string; keepMenuOpen?: boolean }
  ) => void;
};

export function MainLinkItem({
  link,
  isActive,
  isHovering,
  onLinkClick,
  onLinkHover,
  onLinkHoverCancel,
}: MainLinkItemProps) {
  const isWidthBelowXL = useMediaQuery(HEADER_LG_MEDIA_QUERY);
  const hasAnimatedOnceRef = useRef(false);

  const handleMouseEnter = () => {
    onLinkHover(link.item.title);
  };

  const handleMouseLeave = () => {
    onLinkHoverCancel();
  };

  const hasMenuItems = link.menu && link.menu.length > 0;
  const hasCarousel = link.menu.find((group) => group.carousel) !== undefined;
  const hasExtraMenuItems =
    link.menu.find((group) => group.type === 'extra')?.lists?.length ?? 0 > 0;

  if (isActive) {
    hasAnimatedOnceRef.current = true;
  }

  const radioId = `desktop-menu-${(link.item.title || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')}`;

  const toggleLink = () => {
    onLinkClick?.({ title: isActive ? '' : link.item.title || '' });
  };

  const handleClick = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      toggleLink();
    },
    [isActive, link.item.title, onLinkClick]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggleLink();
      }
    },
    [isActive, link.item.title, onLinkClick]
  );

  return (
    <li
      className={cn(
        'group/header-list-item headerLg:p-0 headerLg:focus-within:bg-transparent headerLg:hover-hover:hover:bg-transparent self-stretch transition-colors duration-300',
        !isActive &&
          'hover-hover:hover:bg-gray-dark-100 headerLg:focus-within:[&:has(:focus-visible)]:bg-transparent focus-within:[&:has(:focus-visible)]:bg-gray-dark-100'
      )}
      data-hovered={isHovering && !isActive}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {!isWidthBelowXL && hasMenuItems ? (
        <div className='headerLg:h-full headerLg:m-0 headerLg:border-none border-gray-dark-200 mx-5 border-t group-first/header-list-item:border-none'>
          <div className='desktop-menu-item block h-full'>
            <input
              type='radio'
              name='desktop-menu'
              id={radioId}
              className='desktop-menu-radio sr-only'
              checked={isActive}
              readOnly
              tabIndex={-1}
            />
            <label
              htmlFor={radioId}
              className='block h-full cursor-pointer'
              onClick={handleClick}
              onKeyDown={handleKeyDown}
            >
              <div className='pointer-events-none'>
                <HeaderItem
                  icon={link.item.icon}
                  badge={link.item.badge}
                  title={link.item.title!}
                  href={link.item.href}
                  showDropdown={link.item.showDropdown}
                  expanded={isActive}
                />
              </div>
            </label>
            <div className='megamenu headerLg:grid headerLg:grid-rows-[max-content] bg-base-dark-white headerLg:px-10 headerLg:max-h-[calc(100vh-100px)] absolute left-0 right-0 top-full z-[60] h-max w-full origin-top overflow-hidden overflow-y-auto'>
              <section
                className={cn(
                  'relative mx-auto h-full w-full',
                  getWrapperMaxWidth('mid')
                )}
              >
                <div
                  className={cn(
                    'headerLg:max-h-[calc(100vh-50px)] grid h-full grid-cols-[1fr_min(368px)]'
                  )}
                >
                  {link.menu.map((group, i) => (
                    <div
                      key={i}
                      className={cn(
                        group.lists.length === 0 && !group.carousel && 'hidden',
                        group.type === 'main' && 'headerLg:-ml-3 pb-6',
                        group.type === 'extra' && 'h-full',
                        group.type === 'extra'
                          ? 'col-start-2'
                          : hasExtraMenuItems || hasCarousel
                          ? 'col-start-1'
                          : 'col-[1/-1]'
                      )}
                    >
                      <div className='flex h-full flex-col'>
                        <MenuGroup
                          type={group.type}
                          carousel={group.carousel}
                          lists={group.lists}
                          onLinkClick={onLinkClick}
                        />
                        {group.promoBanners && (
                          <div className='headerLg:pr-6 headerLg:pl-2 flex w-full flex-row gap-6 px-2'>
                            {group.promoBanners.map((banner, i) => (
                              <MenuPromoBanner
                                key={i}
                                {...banner}
                                layout={
                                  (group.promoBanners?.length ?? 0) > 1
                                    ? 'vertical'
                                    : 'horizontal'
                                }
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          </div>
        </div>
      ) : (
        <div className='headerLg:h-full headerLg:m-0 headerLg:border-none border-gray-dark-200 mx-5 border-t group-first/header-list-item:border-none'>
          <HeaderItem
            icon={link.item.icon}
            badge={link.item.badge}
            title={link.item.title!}
            href={link.item.href}
            showDropdown={link.item.showDropdown}
            onItemClick={onLinkClick}
            expanded={isActive}
          />
        </div>
      )}

      {isWidthBelowXL && hasMenuItems && (
        <details className='mobile-menu-accordion' open={isActive}>
          <summary className='sr-only'>{link.item.title} menu</summary>
          <div
            className={cn(
              'grid transition-[grid-template-rows] duration-200 ease-out',
              isActive ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
            )}
          >
            <div className='overflow-hidden'>
              <MenuAccordionContent
                groups={link.menu}
                onLinkClick={onLinkClick}
              />
            </div>
          </div>
        </details>
      )}
    </li>
  );
}
